/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */
import { channel, END } from 'redux-saga';
import {
    take, fork, call, put, select, takeLatest,
} from 'redux-saga/effects';
import mbpLogger from 'mbp-logger';

import * as addToCartActions from './AddToCart-Actions';
import { getFeatureFlags } from '../App/ducks/Config/Config-Selectors';
import { getProfileInfo, getIsAuthenticatedStatus } from '../Member/ducks/Common/Common-Selectors';
import {
    getLocationType, getBrandMovieSkus,
    getSavedAddress, getProductRetailPrice, getUserSubmittedProductFilterZipcode, getDonationsCategory,
} from '../App/App-Selectors';
import {
    setProductRetailPrice, setGCIData, setGCIKey, setUserSubmittedProductFilterZipcode, setLocationType, setUserSelectedAddress,
} from '../App/App-Actions';
import { retrieveCart } from '../MfeCheckout/ducks/Cart/ducks/RetrieveCart/RetrieveCart-Operations';
import { checkJWT } from '../Member/ducks/Auth/Auth-Operations';
import { persistCheckout } from '../../../app/helpers/EnterpriseID/CustomerDataPersistance/customerDataPersistance';
import { getAddToCartGiftMessage, getLastAddedOrderItemDetails } from './AddToCart-Selectors';
import { getBaseCode } from '../../../app/helpers/tracking/product/dataLayerProductHelpers';
import { trackEvent } from '../TagManager/ducks/TagManager/TagManager-Actions';
import { trackCartUpdate } from '../TagManager/ducks/TagManager/TagManager-Helpers';
import { clearAuthDependentState, triggerInterstitialLogin } from '../Member/ducks/Auth/Auth-Actions';
import { getBrand } from '../App/ducks/Brand/Brand-Selectors';
import {
    convertAddressByAddressSource, getFuneralSympathy, getZipByAddressSource, isSCIAddressAutoFillable,
} from './helpers/preCheckoutAddressNormalizer';

// Checkout
import * as commonSelectors from '../Checkout/ducks/Common/Common-Selectors';
import {
    getCartCount,
    getCartOrder,
    getOrderContainsSubscriptionItem,
} from '../MfeCheckout/ducks/Cart/Cart-Selectors';

import { callHandleCartFailure } from '../MfeCheckout/ducks/Cart/Cart-Actions';
import { orderOperations } from '../Order';
import orderClient from '../../../apis/checkout-apis/orderClient';
import cartServices from '../../../apis/cart-service-apis/cartServiceClient';
import { setCGCCustomCardData } from '../CGC/CGC-Actions';
import PayloadGeneratorFromOrderHistory from './helpers/payloadGeneratorFromOrderHistory';
import { mfeSubscribePassportBundle } from '../MfeCheckout/ducks/Passport/Passport-Action';
import { setProfileUserRole } from '../Member/ducks/Profile/Profile-Actions';
import { payloadAddToCartCS } from './helpers/payloadGeneratorClickStreamCart';
import { circuitBreaker } from '../Member/ducks/Auth/helper/helper';
import { setShopTheSiteLinkBackToGiftList } from '../../../app/components/GraphqlComponents/GraphqlProductPage/Partials/common/shopTheSite';
import { getCountryCodeFromTwoToThreeDigit } from '../Checkout/helpers/countryCode';
import getBrandPresentationFamily from '../../../app/helpers/getBrandPresentationFamily';
import { removePmallItem } from '../Common/ducks/PmallPersonalization/PmallPersonalization-Actions';
import { getCheckoutFeatureFlags } from '../Checkout/ducks/App/App-Selectors';
// mbpAddToCart setup
const mbpAddToCartChannel = channel();

export const handleMbpAddToCartTestEvent = (e) => {
    if (e?.detail.brandCode
        && e?.detail.productCode) {
        const { brandCode, productCode, isPassport = false } = e.detail;
        mbpAddToCartChannel.put(addToCartActions.mbpAddToCart(brandCode, productCode, isPassport));
    }
};

// eslint-disable-next-line require-yield
export function* startMbpAddToCartChannel() {
    if (typeof document !== 'undefined') {
        document.addEventListener('mbpAddToCart', handleMbpAddToCartTestEvent);
    }
}

function* addToCartOnEvent() {
    try {
        yield call(startMbpAddToCartChannel);
    } catch (err) {
        mbpLogger.logError({
            function: 'addToCartOnEvent',
            module: 'mbp-pwa-ui',
            message: 'Error injecting mbpAddToCart script for addToCart',
            jsError: err,
        });
    }
}

export function* handleAuthorizationApiFailures(resumeFunction, resumeFunctionParameter) {
    const logOrderId = yield select(commonSelectors.getOrderId);
    const flagState = yield select(commonSelectors.getFlags);

    if (flagState['is-checkout-monitoring-enabled']) {
        mbpLogger.logError({
            appName: process.env.npm_package_name,
            module: 'Auth-Operations.js',
            function: 'handleAuthorizationApiFailures',
            message: 'handleAuthorizationApiFailures start',
            orderId: logOrderId,
        });
    }

    yield put(clearAuthDependentState(true));

    yield call(checkJWT);

    yield call(resumeFunction, resumeFunctionParameter);

    if (flagState['is-checkout-monitoring-enabled']) {
        mbpLogger.logError({
            appName: process.env.npm_package_name,
            module: 'Auth-Operations.js',
            function: 'handleAuthorizationApiFailures',
            message: 'handleAuthorizationApiFailures end',
            orderId: logOrderId,
        });
    }
}

// API
const postAddToCart = ({
    JWT_TOKEN, item, recipients, giftHistoryOrderItemId, personalization,
}) => orderClient.addToCart(
    {},
    JWT_TOKEN,
    recipients,
    item,
    giftHistoryOrderItemId,
    personalization,
);

const postAddAddonsToCart = ({
    JWT_TOKEN,
    addonData,
    orderItemId,
    orderId,
}) => orderClient.addAddonItemsToCart({}, JWT_TOKEN, orderItemId, addonData, orderId);

const postAddWrapupsToCart = ({
    JWT_TOKEN,
    wrapupData,
    orderId,
}) => orderClient.addWrapupsToCart({}, JWT_TOKEN, wrapupData, orderId);

const addRecipient = ({
    JWT_TOKEN, orderItemId, recipient, entryId, orderId,
}) => orderClient.addRecipient(
    {},
    JWT_TOKEN,
    orderItemId,
    recipient,
    entryId,
    orderId,
);

const prepareBuyAgainOrder = async ({
    JWT_TOKEN,
    orderId,
}) => {
    try {
        return await orderClient.orderPrepare({}, JWT_TOKEN, orderId);
    } catch (ex) {
        mbpLogger.logError({
            orderId,
            appName: process.env.npm_package_name,
            module: 'mbp-pwa-ui',
            function: 'prepareBuyAgainOrder',
            jsError: ex,
            message: 'Prepare Cart call',
        });
        return null;
    }
};

// Update Gift Message When Item is added to cart
function* updateGiftMessage(jwtToken, orderId, orderItemId, giftMessage, occasionCode) {
    try {
        yield call(
            orderClient.updateCart,
            {},
            jwtToken,
            orderItemId,
            null,
            null,
            {
                message: giftMessage,
                occasionCode,
            },
            orderId,
        );
    } catch (ex) {
        yield put(callHandleCartFailure(ex));

        mbpLogger.logError({
            orderItem: orderItemId,
            appName: process.env.npm_package_name,
            module: 'mbp-pwa-ui',
            function: 'addGiftMessage',
            jsError: ex,
            message: 'Add gift message',
        });
    }
}

function* getPreCheckoutRecipientAddress() {
    const savedAddress = yield select(getSavedAddress);
    const recipient = yield call(convertAddressByAddressSource, savedAddress);
    const locationType = yield select(getLocationType);
    recipient.locationType = locationType;
    return recipient;
}

function* workerLogAddToCartError(exception) {
    const response = exception?.response || {};
    let addToCartErrorMessage = 'We are sorry, there seems to be a technical issue, please try again';
    const errorMessage = response?.data?.detailedError?.errorMessage || response?.data?.errorMessage;
    const errorMessageList = response?.data?.detailedError?.validationErrors || [];
    if (errorMessage) {
        addToCartErrorMessage = errorMessage;
    }
    yield put(addToCartActions.loadAddToCartError(addToCartErrorMessage, errorMessageList));
}

function* onMbpAddToCart({ brandCode, productCode, isPassport }) {
    const item = {
        brandCode,
        productCode,
        quantity: '1',
    };
    try {
        const JWT_TOKEN = yield call(checkJWT);

        const addToCartResp = yield call(postAddToCart, {
            recipients: null,
            item,
            JWT_TOKEN,
            giftHistoryOrderItemId: '',
            personalization: '',
        });
        const { orderId } = addToCartResp.data;

        // Order Id
        yield call(orderOperations.setOrderId, {
            data: orderId,
        });

        yield call(retrieveCart);
        yield put(addToCartActions.addToCartSuccess());

        if (isPassport) {
            yield put(setProfileUserRole('M'));
        }
    } catch (ex) {
        yield put(callHandleCartFailure(ex));

        mbpLogger.logError({
            item,
            appName: process.env.npm_package_name,
            module: 'Checkout-Operations',
            function: 'onMbpAddToCart',
            jsError: ex,
            message: 'onMbpAddToCart Failed',
        });
    }
}

const circuitBreakAddToCart = circuitBreaker(2);

function* onAddToCart({
    item,
    history,
    categoryId,
    partNumber,
    skipAddons,
    deliveryDate,
    categoryName,
    categoryPath,
    isPassportBundleItem,
    movieSelected,
    selectedSku,
    isPassportBundle,
    handleMiniCartModalClick,
    enableMinicart,
    closeCalendar,
    showWrapup,
    quickviewLocationType,
    location,
}) {
    const args = { item, history };
    const logOrderId = yield select(commonSelectors.getOrderId);
    const flagState = yield select(getFeatureFlags);

    try {
        yield put(addToCartActions.loadAddToCartloader(true));
        yield put(addToCartActions.loadItemAdded(null));
        yield put(addToCartActions.clearAddToCartError());
        mbpLogger.logInfo({
            function: 'onAddToCart',
            appName: process.env.npm_package_name,
            module: 'Checkout-Operations',
            message: 'onAddToCart - Start',
            item,
            orderId: logOrderId,
        });

        const JWT_TOKEN = yield call(checkJWT);
        const brandMovieSkus = yield select(getBrandMovieSkus);

        // use what is going to the backend, set the application state below
        let zipCode = item?.delivery?.zipCode || '';
        if (!zipCode) {
            zipCode = yield select(getUserSubmittedProductFilterZipcode);
        }

        /**
         * Because the category page re-renders based on the location type in the redux store
         * and quickview is on the category page, setting the redux store from quickview modal
         * causes bugs.
         *
         * reconcile the two here.
         */
        let locationType = yield select(getLocationType);
        if (quickviewLocationType) {
            locationType = quickviewLocationType;
        }

        const reqPayload = {
            item,
            JWT_TOKEN,
            giftHistoryOrderItemId: '',
        };

        // Starting at the end of the string (backwards pattern matching), continue to match any characters that aren't '-' or '_'
        if (brandMovieSkus[item.productCode.match(/[^-_]*$/)]) {
            // eslint-disable-next-line no-param-reassign
            item.lineItemType = 'NBCU';
        }

        const preCheckoutSavedAddress = yield select(getSavedAddress);
        if (
            (isSCIAddressAutoFillable(preCheckoutSavedAddress, locationType, zipCode) && preCheckoutSavedAddress.addressSource !== 'expressCheckout')
            || (
                ['expressCheckout', 'buyAgain'].includes(preCheckoutSavedAddress.addressSource)
                && zipCode
                && getZipByAddressSource(preCheckoutSavedAddress) === zipCode
            )
        ) {
            // If there is an address saved in Redux pre checkout
            // (from Guided Nav, address book, SCI)
            // save that as part of add to cart call to autofill in checkout
            // exception is if user comes in on SCI channel but changes the zipcode or location type
            const recipientAddress = yield call(getPreCheckoutRecipientAddress);
            reqPayload.recipients = [recipientAddress];
            if (flagState['is-viewing-time-enabled'] && preCheckoutSavedAddress.addressSource === 'SCI') {
                // eslint-disable-next-line no-param-reassign
                item.sympathy = getFuneralSympathy(preCheckoutSavedAddress?.addressComponents?.FuneralServices);
            }
        } else if (locationType && zipCode && item?.delivery?.zipCode !== '00000') {
            reqPayload.recipients = [{
                zipCode,
                locationType,
            }];
        }

        const addtoCartResp = yield call(postAddToCart, reqPayload);
        const [orderId] = addtoCartResp.data.orderId;

        // Order Id
        yield call(orderOperations.setOrderId, {
            data: addtoCartResp.data.orderId,
        });

        yield call(retrieveCart);

        // Last Added Order Item
        const orderItemId = addtoCartResp?.data?.orderItemId?.[0] || '';

        yield put(addToCartActions.loadItemAdded({
            orderItemId,
            orderItemDetails: item,
        }));

        // setting retail price on redux to use it on checkout flow
        // TODO: Remove this once we will get retail price and sale price into cart api response to use this on checkout flow
        const retailPrice = selectedSku?.prices?.[0]?.value || null;
        if (retailPrice) {
            const listOfRetailPrices = yield select(getProductRetailPrice);
            const retailPriceData = [...listOfRetailPrices, { [orderItemId]: retailPrice }];
            yield put(setProductRetailPrice(retailPriceData));
        }

        if (isPassportBundleItem) {
            yield put(mfeSubscribePassportBundle(item.productCode));
        }

        const giftMessage = yield select(getAddToCartGiftMessage);
        if (giftMessage && giftMessage.message && orderItemId) {
            const giftMessageObj = giftMessage.message;
            const occasionCode = giftMessage.occasionCode || '';

            yield call(updateGiftMessage, JWT_TOKEN, orderId, orderItemId, giftMessageObj, occasionCode);
            yield put(addToCartActions.loadAddToCartGiftMessage('', ''));
        }

        if (orderId !== '' && enableMinicart) {
            yield call(closeCalendar);
            yield call(handleMiniCartModalClick, true);
        }

        // Track Add-to-Cart
        let trackAddToCartPayload;
        const addedItem = yield select(getLastAddedOrderItemDetails);
        if (addedItem.product) {
            const { price, product, itemInfo } = addedItem;
            trackAddToCartPayload = {
                eventName: 'add_to_cart',
                eventCategory: 'Product Page', // TODO: find out from gabby
                eventAction: 'Add to Cart', // TODO: find out from gabby
                eventLabel: product.parentProductName,
                products: [
                    {
                        name: product.parentProductName,
                        partNumber: product.productCode,
                        baseCode: getBaseCode(product.productCode),
                        brand: product.brandCode,
                        sKUs: [{
                            partNumber: product.productCode,
                        }],
                        price,
                        currency: 'USD',
                        quantity: itemInfo.quantity,
                        position: 1,
                        categoryId: categoryId || '',
                        categoryName: categoryName || '',
                        categoryPath: categoryPath || '',
                    },
                ],
            };

            yield put(trackEvent(trackAddToCartPayload));
        }

        if (movieSelected) {
            const movieItem = {
                ...item, brandCode: '1001', productCode: movieSelected.skuSelected.sku, lineItemType: 'NBCU',
            };
            yield call(onAddToCart, { item: movieItem });
        }

        if (isPassportBundle?.enable) {
            yield put(mfeSubscribePassportBundle(isPassportBundle, history));
        }

        if (flagState['is-personalization-recently-viewed-products-enabled']) {
            yield call(persistCheckout, item);
        }

        const clickStreamCartPayload = payloadAddToCartCS({
            location, addToCartResp: addtoCartResp.data, trackPayload: trackAddToCartPayload,
        });
        yield put(addToCartActions.loadAddToCartloader(false));
        yield put(addToCartActions.addToCartSuccess(clickStreamCartPayload));

        mbpLogger.logInfo({
            function: 'onAddToCart',
            appName: process.env.npm_package_name,
            module: 'Checkout-Operations',
            message: 'onAddToCart - success',
            item,
            orderId,
        });

        // Route User Based on Product type to wrap up, addon, shipping or cart page
        // Get the product type according to the current product has been added.
        const catalogEntry = addedItem?.product || {};
        const deliveryType = catalogEntry?.productType || '';
        const isDonationItem = addedItem?.lineItemType === 'DONATION';
        const sympathyValue = catalogEntry?.sympathyValue === '1' || catalogEntry?.sympathyValue === '2';
        const internationalCountryCode = addedItem?.deliveryInfo?.countryCode || 'USA';
        const isInternationalProduct = !['USA', 'CAN'].includes(internationalCountryCode);

        // The following if statements are for 18F specific navigation.
        // If the 18F specific params are not passed, the user will be navigated to the cart page as per the previous saga behavior
        if (!enableMinicart) {
            const shouldShowAddonsPage = history
                && deliveryType === 'FPT'
                && !skipAddons
                && !isDonationItem
                && flagState['is-addons-enabled-from-pdp']
                && !(flagState['is-funeral-home-skip-addons-enabled']
                    && ['Funeral home', 'Church'].includes(locationType))
                && !isInternationalProduct
                // subscription product flow
                && !(typeof (item.subscription) !== 'undefined'
                    && flagState['is-subscriptions-skipping-addons']);

            const shouldShowWrapUpPage = history
                && deliveryType === 'GPT'
                && !['Funeral home', 'Church'].includes(locationType)
                && !skipAddons
                && !isDonationItem
                && flagState['is-wrapup-enabled']
                && !sympathyValue
                && !isInternationalProduct
                && showWrapup !== 'false'
                // subscription product flow
                && !(typeof (item.subscription) !== 'undefined'
                    && flagState['is-subscriptions-skipping-addons']);

            if (shouldShowAddonsPage) {
                yield call(history.push, '/add-ons', {
                    categoryId, partNumber, deliveryDate, showWrapup,
                });
            } else if (shouldShowWrapUpPage) {
                yield call(history.push, '/wrap-up', { categoryId, partNumber, deliveryDate });
            } else if (history) {
                // User is Remembered ?
                const userProfile = yield select(getProfileInfo);
                const isAuth = yield select(getIsAuthenticatedStatus);
                const cartCount = yield select(getCartCount);
                const orderContainsSubscriptionItem = yield select(getOrderContainsSubscriptionItem);

                let isRememberedUser = false;
                if (userProfile.email) {
                    isRememberedUser = true;
                }

                if (orderContainsSubscriptionItem.length > 0 && flagState['is-subscriptions-skipping-addons'] && !isAuth) {
                    yield put(triggerInterstitialLogin(history, true));
                } else if (flagState['is-native-interstitial-page-enabled'] && !isAuth && !isRememberedUser) {
                    yield put(triggerInterstitialLogin(history));
                } else if (cartCount > 1) {
                    yield call(history.push, `/checkout/cart/${orderId}`);
                } else {
                    yield call(history.push, `/checkout/shipping/${orderId}`);
                }
            }
        }
        // sync application with what went to the backend
        yield put(setUserSubmittedProductFilterZipcode(zipCode));
        yield put(setLocationType(locationType));

        // Retrive current cart
        // Send cart snapshot to cart track event
        const fdOrder = yield select(getCartOrder);

        yield call(trackCartUpdate, flagState, 'EventAddToCart', fdOrder);
    } catch (ex) {
        yield put(callHandleCartFailure(ex));

        mbpLogger.logError({
            item,
            history,
            appName: process.env.npm_package_name,
            module: 'Checkout-Operations',
            function: 'onAddToCart',
            jsError: ex,
            message: 'onAddToCart Failed',
            orderId: logOrderId,
        });

        const { response } = ex;
        if (response
            && response.status
            && ['401', '403'].includes(response.status.toString())
            && !circuitBreakAddToCart()) {
            mbpLogger.logError({
                function: 'onAddToCart',
                appName: process.env.npm_package_name,
                module: 'Checkout-Operations',
                message: `onAddToCart Failed - Status code: ${response.status} proceeding to get access token`,
                orderId: logOrderId,
            });
            yield call(handleAuthorizationApiFailures, onAddToCart, args);
        }

        yield put(addToCartActions.loadAddToCartloader(false));
        yield put(addToCartActions.addToCartFailed(ex));
    }
}

function* routeAfterAddingAddonWrapUp(history) {
    const orderId = yield select(commonSelectors.getOrderId);
    const isAuthenticated = yield select(getIsAuthenticatedStatus);
    const orderContainsSubscriptionItem = yield select(getOrderContainsSubscriptionItem);

    const flagState = yield select(getFeatureFlags);
    const cartCount = yield select(getCartCount);
    const userProfile = yield select(getProfileInfo);

    let isRememberedUser = false;
    if (userProfile?.email) {
        isRememberedUser = true;
    }

    if (history) {
        if (orderContainsSubscriptionItem.length > 0 && !isAuthenticated) {
            yield put(triggerInterstitialLogin(history, true));
        } else if (
            flagState['is-native-interstitial-page-enabled']
            && !isAuthenticated
            && !isRememberedUser
        ) {
            yield put(triggerInterstitialLogin(history));
        } else if (cartCount > 1) { // redirect added to handle case where there is one item in the cart
            yield call(history.push, `/checkout/cart/${orderId}`);
        } else {
            yield call(history.push, `/checkout/shipping/${orderId}`);
        }
    }
}

function* onAddAddonsToCart({
    history,
    addonData,
    orderItemId,
    selectedAddOnItems,
}) {
    const orderId = yield select(commonSelectors.getOrderId);
    const flagState = yield select(getFeatureFlags);
    try {
        mbpLogger.logInfo({
            function: 'onAddAddonItemsToCart',
            appName: process.env.npm_package_name,
            module: 'Checkout-Operations',
            message: 'onAddAddonItemsToCart - Start',
            addonData,
            orderItemId,
        });

        yield put(addToCartActions.clearAddToCartError());
        yield put(addToCartActions.loadAddToCartloader(true));

        const JWT_TOKEN = yield call(checkJWT);

        yield call(postAddAddonsToCart, {
            addonData,
            orderItemId,
            JWT_TOKEN,
            orderId,
        });

        yield call(retrieveCart);

        // Track Add Add on to cart
        yield put(trackEvent({
            eventName: 'add_to_cart',
            eventCategory: 'Shopping',
            eventAction: 'Product AddOn Added',
            products: Object.keys(selectedAddOnItems).map((key) => {
                const {
                    name, groupName, brandId, partNumber, prices, currency,
                } = selectedAddOnItems[key];

                return {
                    name: groupName || name || '',
                    partNumber,
                    brand: brandId || '',
                    baseCode: getBaseCode(partNumber),
                    sKUs: [{
                        partNumber,
                    }],
                    prices,
                    currency: currency || 'USD',
                    quantity: '1',
                    position: 1,
                    categoryName: 'AddOns' || '',
                };
            }),
        }));

        yield put(addToCartActions.loadAddToCartloader(false));
        yield put(addToCartActions.addToCartSuccess());

        mbpLogger.logInfo({
            function: 'onAddAddonItemsToCart',
            appName: process.env.npm_package_name,
            module: 'Checkout-Operations',
            message: 'onAddAddonItemsToCart - success',
            addonData,
            orderItemId,
        });

        // Retrive current cart
        // Send cart snapshot to cart track event
        const fdOrder = yield select(getCartOrder);
        yield call(trackCartUpdate, flagState, 'EventAddToCart', fdOrder);
    } catch (ex) {
        mbpLogger.logError({
            addonData,
            history,
            appName: process.env.npm_package_name,
            module: 'Checkout-Operations',
            function: 'onAddAddonItemsToCart',
            jsError: ex,
            message: 'onAddAddonItemsToCart Failed',
            orderItemId,
        });

        yield put(addToCartActions.loadAddToCartloader(false));
        yield put(addToCartActions.addToCartFailed(ex));
    }

    if (history) {
        yield call(routeAfterAddingAddonWrapUp, history);
    }
}

function* onAddWrapupsToCart({ wrapupData, history }) {
    const orderId = yield select(commonSelectors.getOrderId);
    try {
        mbpLogger.logInfo({
            function: 'onAddWrapupsToCart',
            appName: process.env.npm_package_name,
            module: 'Checkout-Operations',
            message: 'onAddWrapupsToCart - Start',
            wrapupData,
            orderId,
        });
        yield put(addToCartActions.clearAddToCartError());
        yield put(addToCartActions.loadAddToCartloader(true));

        const JWT_TOKEN = yield call(checkJWT);

        yield call(postAddWrapupsToCart, {
            wrapupData,
            JWT_TOKEN,
            orderId,
        });

        yield call(retrieveCart);

        yield put(addToCartActions.addToCartSuccess());

        mbpLogger.logInfo({
            function: 'onAddWrapupsToCart',
            appName: process.env.npm_package_name,
            module: 'Checkout-Operations',
            message: 'onAddWrapupsToCart - success',
            wrapupData,
            orderId,
        });

        const flagState = yield select(getFeatureFlags);

        // Retrive current cart
        // Send cart snapshot to cart track event
        const fdOrder = yield select(getCartOrder);
        yield call(trackCartUpdate, flagState, 'EventAddToCart', fdOrder);
        yield call(routeAfterAddingAddonWrapUp, history);
    } catch (ex) {
        mbpLogger.logError({
            wrapupData,
            history,
            appName: process.env.npm_package_name,
            module: 'Checkout-Operations',
            function: 'onAddWrapupsToCart',
            jsError: ex,
            message: 'onAddWrapupsToCart Failed',
            orderId,
        });

        yield put(addToCartActions.loadAddToCartloader(false));
        yield put(addToCartActions.addToCartFailed(ex));
    }
}

function* onAddAddonsAndWrapupToCart({
    addonData, history, orderItemId, selectedAddOnItems, wrapupData,
}) {
    const orderId = yield select(commonSelectors.getOrderId);
    const flagState = yield select(getFeatureFlags);
    try {
        yield put(addToCartActions.loadAddToCartloader(false));
        yield put(addToCartActions.clearAddToCartError());

        mbpLogger.logInfo({
            function: 'onAddAddonsAndWrapupToCart',
            appName: process.env.npm_package_name,
            module: 'Checkout-Operations',
            message: 'onAddAddonsAndWrapupToCart - Start',
            addonData,
            orderItemId,
        });

        const JWT_TOKEN = yield call(checkJWT);

        if (addonData) {
            yield call(postAddAddonsToCart, {
                addonData,
                orderItemId,
                JWT_TOKEN,
                orderId,
            });
        }

        if (wrapupData) {
            yield call(postAddWrapupsToCart, {
                wrapupData,
                JWT_TOKEN,
                orderId,
            });
        }

        yield call(retrieveCart);

        // Track Add to Cart
        yield put(trackEvent({
            eventName: 'add_to_cart',
            eventCategory: 'Shopping',
            eventAction: 'Product AddOn Added',
            products: Object.keys(selectedAddOnItems).map((key) => {
                const {
                    name, groupName, brandId, partNumber, prices, currency,
                } = selectedAddOnItems[key];

                return {
                    name: groupName || name || '',
                    partNumber,
                    brand: brandId || '',
                    sKUs: [{
                        partNumber,
                    }],
                    prices,
                    currency: currency || 'USD',
                    quantity: '1',
                    position: 1,
                    categoryName: 'AddOns' || '',
                };
            }),
        }));

        mbpLogger.logInfo({
            function: 'onAddAddonItemsToCart',
            appName: process.env.npm_package_name,
            module: 'Checkout-Operations',
            message: 'onAddAddonItemsToCart - success',
            addonData,
            orderItemId,
        });

        yield put(addToCartActions.loadAddToCartloader(false));
        yield put(addToCartActions.addToCartSuccess());

        // Retrive current cart
        // Send cart snapshot to cart track event
        const fdOrder = yield select(getCartOrder);
        yield call(trackCartUpdate, flagState, 'EventAddToCart', fdOrder);
    } catch (ex) {
        mbpLogger.logError({
            addonData,
            history,
            appName: process.env.npm_package_name,
            module: 'Checkout-Operations',
            function: 'onAddAddonItemsToCart',
            jsError: ex,
            message: 'onAddAddonItemsToCart Failed',
            orderItemId,
        });

        yield put(addToCartActions.loadAddToCartloader(false));
        yield put(addToCartActions.addToCartFailed(ex));
    }

    yield call(routeAfterAddingAddonWrapUp, history);
}

const circuitBreakAddToCartFood = circuitBreaker(2);

function* onAddToCartFood({
    recipients = [], // Set a default value to prevent object destructuring error args = { recipients, item, history };
    item,
    history,
    categoryId,
    partNumber,
    categoryName,
    categoryPath,
    enableMinicart,
    handleMiniCartModalClick,
    isMobile,
    movieSelected,
    eventType,
    passportBundle,
}) {
    const logOrderId = yield select(commonSelectors.getOrderId);
    const args = { recipients, item, history };
    const brand = yield select(getBrand) || {};
    let itemRecipient = recipients;

    try {
        yield put(addToCartActions.loadAddToCartloader(true));
        yield put(addToCartActions.clearAddToCartError());

        mbpLogger.logInfo({
            function: 'onAddToCartFood',
            appName: process.env.npm_package_name,
            module: 'Checkout-Operations',
            message: 'onAddToCartFood - Start',
            item,
            orderId: logOrderId,
        });

        const JWT_TOKEN = yield call(checkJWT);

        // Add Gift List recipient
        let giftHistoryOrderItemId = '';
        let giftContactId = '';
        if (item.giftList && item.giftList.contactId) {
            giftContactId = item.giftList.contactId;
            // giftHistoryOrderItemId is used in 4.1
            giftHistoryOrderItemId = item.giftList.orderItemId;
            // xOrderAttrValues is used in 4.2
            const { xOrderAttrValues } = item.giftList;

            if (Array.isArray(item.attributes)) {
                Object.keys(xOrderAttrValues).forEach((key) => {
                    // Converting xOrderAttrValues object to item.attributes: [{ attributeName, attributeValue }, ...]
                    item.attributes.push({
                        attributeName: key,
                        attributeValue: xOrderAttrValues[key],
                    });
                });
            }

            if (Array.isArray(recipients)) {
                const {
                    address: {
                        company,
                        street: address1,
                        apt: address2,
                        location,
                        city,
                        state,
                        country,
                        zipcode: zipCode,
                    },
                    firstname: firstName,
                    lastname: lastName,
                } = item.giftList;

                const recipient = {
                    address1,
                    address2,
                    city,
                    country: country.id,
                    email: '',
                    firstName,
                    lastName,
                    locationType: location.name,
                    organizationName: company,
                    phone: '',
                    state: state.id,
                    zipCode,
                    isTelephoneMadatory: 'n',
                    isAddressVerified: 'V', // V - Verified
                    dpvIndicator: 'Y',
                    nameOfLocation: '',
                    useAddressForAll: false,
                    checkSameAsDeliveryAddress: false,
                };
                recipients.push(recipient);
            }
        }

        // use what is going to the backend, set the application state below
        let zipCode = item?.delivery?.zipCode || '';
        if (!zipCode) {
            zipCode = yield select(getUserSubmittedProductFilterZipcode);
        }

        const preCheckoutSavedAddress = yield select(getSavedAddress);
        if (
            ['expressCheckout', 'buyAgain'].includes(preCheckoutSavedAddress.addressSource)
            && zipCode
            && getZipByAddressSource(preCheckoutSavedAddress) === zipCode
        ) {
            const recipientAddress = yield call(getPreCheckoutRecipientAddress);
            itemRecipient = [recipientAddress];
        }

        const addtoCartResp = yield call(postAddToCart, {
            recipients: itemRecipient,
            item,
            JWT_TOKEN,
            giftHistoryOrderItemId,
        });

        const [orderId] = addtoCartResp.data.orderId;

        yield call(orderOperations.setOrderId, {
            data: addtoCartResp.data.orderId,
        });

        yield call(retrieveCart);

        // Clear Shop the Site data and set RETURN TO GIFT LIST link on mini cart
        // and remove Shop The Site floating banner
        if (giftContactId) {
            /**
             * Below Save Recipient address by using save recipient address API is used
             * to avoid issue with populating Delivery date on shipping page
             * This would be needed if the add to cart API saves the recipient address and cart returns the delivery date
             */
            if (orderId !== '' && recipients && recipients.length && addtoCartResp.data?.orderItemId?.length) {
                const reqObj = {
                    wcEnv: {},
                    JWT_TOKEN,
                    orderItemId: addtoCartResp.data.orderItemId[0],
                    recipient: recipients[0],
                    entryId: null,
                    orderId,
                };
                yield call(addRecipient, reqObj);
            }

            yield call(setShopTheSiteLinkBackToGiftList, giftContactId);
        }

        // Clean CGI personalization after add to cart (TPF)
        if (item?.personalization) {
            yield put(setGCIData({}));
            yield put(setGCIKey(''));
            yield put(setCGCCustomCardData({}));
        }

        if (orderId !== '' && enableMinicart) {
            yield call(handleMiniCartModalClick, true);
        }

        // Last Added Order Item
        const orderItemId = addtoCartResp?.data?.orderItemId?.[0] || '';
        yield put(addToCartActions.loadItemAdded({
            orderItemId,
            orderItemDetails: item,
        }));

        const flagState = yield select(getFeatureFlags);

        if (movieSelected) {
            const movieItem = { ...item, brandCode: brand.id, productCode: movieSelected.skuSelected.sku };
            yield call(onAddToCart, { item: movieItem });
        }

        if (passportBundle?.enable) {
            yield put(mfeSubscribePassportBundle(passportBundle, history));
        }

        if (flagState['is-personalization-recently-viewed-products-enabled']) {
            yield call(persistCheckout, item);
        }

        mbpLogger.logInfo({
            function: 'onAddToCartFood',
            appName: process.env.npm_package_name,
            module: 'Checkout-Operations',
            message: 'onAddToCartFood - success',
            item,
            orderId,
        });

        // Track Add-to-Cart
        let trackAddToCartPayload;
        const addedItem = yield select(getLastAddedOrderItemDetails);
        if (addedItem && addedItem.product) {
            const { price, product, itemInfo } = addedItem;
            trackAddToCartPayload = {
                eventName: 'add_to_cart',
                eventCategory: eventType || 'Product Page', // TODO: find out from gabby
                eventAction: 'Add to Cart', // TODO: find out from gabby
                eventLabel: product.parentProductName,
                products: [
                    {
                        name: product.parentProductName,
                        partNumber: product.productCode,
                        baseCode: getBaseCode(product.productCode),
                        brand: product.brandCode,
                        sKUs: [{
                            partNumber: product.productCode,
                        }],
                        price,
                        quantity: itemInfo.quantity,
                        currency: 'USD',
                        position: 1,
                        categoryId: categoryId || '',
                        categoryName: categoryName || '',
                        categoryPath: categoryPath || '',
                    },
                ],
            };
            console.log('trackAddToCartPayload - checkout', trackAddToCartPayload);
            yield put(trackEvent(trackAddToCartPayload));
        }

        yield put(addToCartActions.loadAddToCartloader(false));
        const clickstreamPayload = payloadAddToCartCS({ addToCartResp: addtoCartResp.data, trackPayload: trackAddToCartPayload });
        yield put(addToCartActions.addToCartSuccess(clickstreamPayload));

        // Route user after item added to cart
        const locationType = yield select(getLocationType);
        const cartCount = yield select(getCartCount);
        const sympathy = addedItem?.product?.sympathyValue || '';
        const sympathyValue = sympathy === '1' || sympathy === '2';
        const donationsCategoryValue = yield select(getDonationsCategory);
        const isDonationsCategoryValue = donationsCategoryValue?.toLowerCase() === 'donations';
        let miniCartFlag = enableMinicart;

        /**
         * for AB test wrapup we need to handle minicart flag.
         * eg: if 'is-wrapup-enabled' enabled at 50% and mini cart enabled 50% it's hard to get same flag states from LD.
         * is-wrapup-enabled will fail if Traffic A(50%) get true and Same Traffic gets minicart enabled.
         */
        if (flagState['is-wrapup-enabled']) {
            miniCartFlag = false;
        }
        //  ^^^^ Important: add the location.pathname in the conditional because
        // because the ab test above is affecting add to cart inside shipping (flash deals)
        // is making user redirect to wrap up

        if (
            (!miniCartFlag || isMobile) && history
            && history.location.pathname?.indexOf('/checkout/shipping') < 0
        ) {
            if (!isDonationsCategoryValue && !['Funeral home', 'Church'].includes(locationType)
                && flagState['is-wrapup-enabled']
                && preCheckoutSavedAddress?.addressSource !== 'SCI'
                && !sympathyValue
                // subscription product flow
                && !(typeof (item.subscription) !== 'undefined'
                    && flagState['is-subscriptions-skipping-addons'])) {
                yield call(history.push, '/wrap-up', { categoryId, partNumber });
            } else if (flagState['which-checkout-variation'] === 'floral' && cartCount > 1) {
                yield call(history.push, `/checkout/cart/${orderId || ''}`);
            } else if (flagState['which-checkout-variation'] === 'food' && flagState['is-interstitial-login-enabled']) {
                yield put(triggerInterstitialLogin(history));
            } else {
                yield call(history.push, `/checkout/shipping/${orderId || ''}`);
            }
        }
        // Retrive current cart
        // Send cart snapshot to cart track event
        const fdOrder = yield select(getCartOrder);
        yield call(trackCartUpdate, flagState, 'EventAddToCart', fdOrder);
    } catch (ex) {
        yield put(callHandleCartFailure(ex));

        mbpLogger.logError({
            recipients,
            item,
            history,
            appName: process.env.npm_package_name,
            module: 'Checkout-Operations',
            function: 'onAddToCartFood',
            jsError: ex,
            message: 'onAddToCartFood Failed',
            orderId: logOrderId,
        });

        const { response } = ex;
        if (response
            && response.status
            && ['401', '403'].includes(response.status.toString())
            && !circuitBreakAddToCartFood()) {
            mbpLogger.logError({
                function: 'onAddToCartFood',
                appName: process.env.npm_package_name,
                module: 'Checkout-Operations',
                message: `onAddToCartFood Failed - Status code: ${response.status} proceeding to get access token`,
                orderId: logOrderId,
            });
            yield call(handleAuthorizationApiFailures, onAddToCartFood, args);
        }

        yield put(addToCartActions.loadAddToCartloader(false));
        yield put(addToCartActions.addToCartFailed(ex));
    }
}

const circuitBreakBuyAgain = circuitBreaker(2);

function* workerOnBuyAgain(data) {
    try {
        const { orders, history } = data;
        const JWT_TOKEN = yield call(checkJWT);
        const flagState = yield select(getFeatureFlags);
        yield put(addToCartActions.loadAddToCartloader(true));
        yield put(addToCartActions.loadItemAdded(null));
        yield put(addToCartActions.clearAddToCartError());

        if (orders?.length) {
            let pmallProductSku = null;

            for (let i = 0; i < orders?.length; i += 1) {
                const orderDetails = orders[i];
                const { items: itemDetailList } = orderDetails;
                const recipientDetail = orderDetails?.recipientProfiles?.[0] || {};

                if (itemDetailList?.length) {
                    const payloadGenerator = yield new PayloadGeneratorFromOrderHistory();
                    // Get payload item object from payload generator
                    const item = yield call(payloadGenerator.generatePayloadFromOrderHistoryItem, {
                        itemDetailList, // getting first index for now
                        recipient: recipientDetail,
                    });

                    // get recipients from payload generator
                    const recipients = yield call(payloadGenerator.getRecipients);
                    const reqPayload = {
                        item,
                        recipients,
                        JWT_TOKEN,
                        giftHistoryOrderItemId: '',
                    };

                    if (['1030', '1033'].includes(item.brandCode)) { // Pmall products
                        yield put(setUserSelectedAddress({
                            checkoutAutoFill: true,
                            addressSource: 'buyAgain',
                            recipient: recipients?.[0],
                        }));
                        yield put(setUserSubmittedProductFilterZipcode(recipients?.[0]?.zipCode));

                        pmallProductSku = `${item.brandCode}-${item.productCode}`;
                    } else {
                        const addtoCartResp = yield call(postAddToCart, reqPayload);
                        yield call(orderOperations.setOrderId, {
                            data: addtoCartResp?.data?.orderId,
                        });
                    }
                }
                if (!pmallProductSku) {
                    const orderId = yield select(commonSelectors.getOrderId);

                    yield call(retrieveCart);

                    // order prepare call -- this generates delivery date for product
                    yield call(prepareBuyAgainOrder, { JWT_TOKEN, orderId });

                    yield put(addToCartActions.loadAddToCartloader(false));
                    yield put(addToCartActions.addToCartSuccess());
                    // redirect user to shipping page
                    if (history) {
                        yield call(history.push, `/checkout/shipping/${orderId}`);
                    }
                } else {
                    yield call(history.push, `/searchterm/${pmallProductSku}`);
                }
            }
        }
        // Retrive current cart
        // Send cart snapshot to cart track event
        const fdOrder = yield select(getCartOrder);
        yield call(trackCartUpdate, flagState, 'EventAddToCart', fdOrder);
    } catch (ex) {
        yield put(callHandleCartFailure(ex));

        mbpLogger.logError({
            function: 'workerOnBuyAgain',
            appName: process.env.npm_package_name,
            module: 'AddToCart-Operations.js',
            jsError: ex,
        });

        const { response } = ex;
        if (response
            && response.status
            && ['401', '403'].includes(response.status.toString())
            && !circuitBreakBuyAgain()) {
            mbpLogger.logError({
                function: 'workerOnBuyAgain',
                appName: process.env.npm_package_name,
                module: 'Checkout-Operations',
                message: `onAddToCart Failed - Status code: ${response.status} proceeding to get access token`,
            });
            yield call(handleAuthorizationApiFailures, onAddToCart, data);
        }

        yield put(addToCartActions.loadAddToCartloader(false));
        yield put(addToCartActions.addToCartFailed(ex));
    }
}

function* addToCartPmallAddons({ jwtToken, products }) {
    try {
        yield call(cartServices.addToCart, {
            env: {},
            jwtToken,
            products,
        });
    } catch (err) {
        mbpLogger.logError({
            function: 'addToCartPmallAddons',
            module: 'mbp-pwa-ui',
            message: 'Error addToCart pmall addons',
            jsError: err,
        });
    }
}

function* workerAddToCartNewCartServices(action) {
    try {
        const {
            addonsAsProduct,
            categoryId,
            categoryName,
            categoryPath,
            closeCalendar,
            deliveryDate,
            enableMinicart,
            eventType,
            handleMiniCartModalClick,
            history,
            passportBundle,
            item, // array - main product
            movieSelected,
            partNumber,
            quickviewLocationType,
            showWrapup,
            skipAddons,
            location,
        } = action.payload;

        yield put(addToCartActions.loadAddToCartloader(true));
        yield put(addToCartActions.clearAddToCartError());

        // Item in position 0 is the main item
        if (item?.length > 0) {
            const jwtToken = yield call(checkJWT);

            // use what is going to the backend, set the application state below
            let zipCode = item?.delivery?.zipCode || '';
            if (!zipCode) {
                zipCode = yield select(getUserSubmittedProductFilterZipcode);
            }

            /**
             * Because the category page re-renders based on the location type in the redux store
             * and quickview is on the category page, setting the redux store from quickview modal
             * causes bugs.
             *
             * reconcile the two here.
             */
            let locationType = yield select(getLocationType);
            if (quickviewLocationType) {
                locationType = quickviewLocationType;
            }

            const reqPayload = {
                env: {},
                jwtToken,
                products: item,
            };

            const preCheckoutSavedAddress = yield select(getSavedAddress);
            const productFamily = getBrandPresentationFamily(item?.[0]?.brandCode || '1001');

            if (
                item?.[0]?.delivery?.zipCode !== '00000'
                && productFamily === 'flower'
                && (
                    (
                        isSCIAddressAutoFillable(preCheckoutSavedAddress, locationType, zipCode)
                        && !['buyAgain'].includes(preCheckoutSavedAddress.addressSource)
                    )
                    || (
                        ['buyAgain'].includes(preCheckoutSavedAddress.addressSource)
                        && zipCode
                        && getZipByAddressSource(preCheckoutSavedAddress) === zipCode
                    )
                )
            ) {
                // If there is an address saved in Redux pre checkout
                // (from Guided Nav, address book)
                // save that as part of add to cart call to autofill in checkout
                const recipientAddress = yield call(getPreCheckoutRecipientAddress);
                if (recipientAddress.address1) {
                //  New API need three letter country
                    let country = recipientAddress.country;
                    if (country.length === 2) {
                        country = getCountryCodeFromTwoToThreeDigit(recipientAddress.country);
                    }

                    reqPayload.products[0].recipientInfo = {
                        addressId: recipientAddress.addressId,
                        entryId: recipientAddress.addressId,
                        recipient: {
                            ...recipientAddress,
                            country,
                            isAddressVerified: recipientAddress.isAddressVerified === 'V',
                            isAddressUpdate: false,
                            addressStatus: recipientAddress.isAddressVerified,
                        },
                    };
                }
            } else if (
                locationType
                && zipCode
                && item?.[0]?.delivery?.zipCode !== '00000'
                && productFamily === 'flower'
            ) {
                reqPayload.products[0].recipientInfo = {
                    addressId: '',
                    entryId: '',
                    recipient: {
                        zipCode,
                        locationType,
                    },
                };
            }

            const giftMessage = yield select(getAddToCartGiftMessage);

            if (giftMessage && giftMessage.message) {
                reqPayload.products[0].greeting = {
                    ...giftMessage,
                };
                yield put(addToCartActions.loadAddToCartGiftMessage('', ''));
            }

            /*
             * Add to Cart Main product
             */
            const addtoCartResp = yield call(cartServices.addToCart, reqPayload);

            const cartId = addtoCartResp.data.cartId;

            yield call(orderOperations.setOrderId, {
                data: [addtoCartResp.data.cartId],
            });

            // Add to cart Pmall addons as extra products
            if (addonsAsProduct?.length > 0) {
                yield call(addToCartPmallAddons, {
                    jwtToken,
                    products: addonsAsProduct,
                });
            }

            // Adding  NBCU Products
            if (movieSelected) {
                const movieItem = [{
                    brandCode: '1001',
                    productCode: movieSelected.skuSelected.sku,
                    lineItemType: 'NBCU',
                }];

                yield call(cartServices.addToCart, {
                    env: {},
                    jwtToken,
                    products: movieItem,
                });
            }

            // Addins passport bundles
            if (passportBundle?.enable) {
                yield put(mfeSubscribePassportBundle(passportBundle, history));
            }

            yield call(retrieveCart);

            const flagState = yield select(getFeatureFlags);

            // TODO: (July-12-2023) - Need to check if this code for retail price is really  need since
            // checkout is in MFE now. Taked from the function onAddToCart

            // setting retail price on redux to use it on checkout flow
            // TODO: Remove this once we will get retail price and sale price into cart api response to use this on checkout flow
            // const retailPrice = selectedSku?.prices?.[0]?.value || null;
            // if (retailPrice) {
            //     const listOfRetailPrices = yield select(getProductRetailPrice);
            //     const retailPriceData = [...listOfRetailPrices, { [orderItemId]: retailPrice }];
            //     yield put(setProductRetailPrice(retailPriceData));
            // }
            // TODO: (July-12-2023)

            if (cartId !== '' && enableMinicart && handleMiniCartModalClick) {
                if (closeCalendar) {
                    yield call(closeCalendar);
                }
                yield call(handleMiniCartModalClick, true);
            }

            mbpLogger.logInfo({
                function: 'onAddToCartNewCartServices',
                appName: process.env.npm_package_name,
                module: 'AddToCart-Operations',
                message: 'onAddToCartNewCartServices - success',
                item,
                cartId,
            });

            // Track Add-to-Cart
            let trackAddToCartPayload;
            const addedItem = yield select(getLastAddedOrderItemDetails);
            console.log('item', item);
            if (addedItem && addedItem.product) {
                const { itemInfo, product } = addedItem;
                let pid = product?.productCode;
                if (flagState?.['is-product-id-consistent-events-enabled']) {
                    pid = product?.parentProductCode;
                }

                trackAddToCartPayload = {
                    eventName: 'add_to_cart',
                    eventCategory: eventType || 'Product Page', // TODO: find out from gabby
                    eventAction: 'Add to Cart', // TODO: find out from gabby
                    eventLabel: product.parentProductName,
                    products: [
                        {
                            name: product.parentProductName,
                            partNumber: pid,
                            baseCode: flagState?.['is-product-id-consistent-events-enabled'] ? getBaseCode(pid)  : getBaseCode(product.productCode),
                            brand: product.brandCode,
                            variant: product?.productCode,
                            sKUs: [{
                                partNumber: pid,
                            }],
                            price: itemInfo.price,
                            quantity: itemInfo.quantity,
                            currency: itemInfo.currency || 'USD',
                            position: 1,
                            categoryId: categoryId || '',
                            categoryName: categoryName || '',
                            categoryPath: categoryPath || '',
                        },
                    ],
                };

                yield put(trackEvent(trackAddToCartPayload));
            }

            if (flagState['is-personalization-recently-viewed-products-enabled']) {
                yield call(persistCheckout, item);
            }

            if (action?.payload?.handleCallBack) {
                yield call(action.payload.handleCallBack, addedItem.cartItemId);
            }

            const clickstreamPayload = payloadAddToCartCS({
                location,
                addToCartResp: addtoCartResp.data,
                trackPayload: trackAddToCartPayload,
                zipCode: item?.delivery?.zipCode,
            });

            yield put(addToCartActions.loadAddToCartloader(false));
            yield put(addToCartActions.addToCartSuccess(clickstreamPayload));

            mbpLogger.logInfo({
                function: 'workerAddToCartNewCartServices',
                appName: process.env.npm_package_name,
                module: 'AddToCart-Operations',
                message: 'workerAddToCartNewCartServices - success',
                item,
                orderId: cartId,
            });

            // Route User Based on Product type to wrap up, addon, shipping or cart page
            // Get the product type according to the current product has been added.
            const productItem = addedItem?.product || {};
            const deliveryType = productItem?.productType || '';
            const isDonationItem = addedItem?.lineItemType === 'DONATION';
            const sympathyValue = productItem?.sympathyValue === '1' || productItem?.sympathyValue === '2';
            const internationalCountryCode = addedItem?.deliveryInfo?.countryCode || 'USA';
            const isInternationalProduct = !['USA', 'CAN'].includes(internationalCountryCode);
            const checkoutFeatureFlags = yield select(getCheckoutFeatureFlags);

            // The following if statements are for 18F specific navigation.
            // If the 18F specific params are not passed, the user will be navigated to the cart page as per the previous saga behavior

            if (!enableMinicart) {
                const shouldShowAddonsPage = history
                && deliveryType === 'FPT'
                && !skipAddons
                && !isDonationItem
                && flagState['is-addons-enabled-from-pdp']
                && !(flagState['is-funeral-home-skip-addons-enabled']
                    && ['Funeral home', 'Church'].includes(locationType))
                && !isInternationalProduct
                // subscription product flow
                && !(typeof (item[0].subscription) !== 'undefined'
                && flagState['is-subscriptions-skipping-addons'])
                    && !item[0].isOneClickExperience;
                const shouldShowWrapUpPage = history
                && deliveryType === 'GPT'
                && !['Funeral home', 'Church'].includes(locationType)
                && !skipAddons
                && !isDonationItem
                && flagState['is-wrapup-enabled']
                && !sympathyValue
                && !isInternationalProduct
                && showWrapup !== 'false'
                // subscription product flow
                && !(typeof (item[0].subscription) !== 'undefined'
                && flagState['is-subscriptions-skipping-addons'])
                    && !item[0].isOneClickExperience;

                if (shouldShowAddonsPage) {
                    yield call(history.push, '/add-ons', {
                        categoryId, partNumber, deliveryDate, showWrapup, atcAction: flagState['is-atc-smooth-transition-enabled'],
                    });
                } else if (shouldShowWrapUpPage) {
                    yield call(history.push, '/wrap-up', { categoryId, partNumber, deliveryDate });
                } else if (history) {
                // User is Remembered ?
                    const userProfile = yield select(getProfileInfo);
                    const isAuth = yield select(getIsAuthenticatedStatus);
                    const cartCount = yield select(getCartCount);
                    const orderContainsSubscriptionItem = yield select(getOrderContainsSubscriptionItem);

                    let isRememberedUser = false;
                    if (userProfile.email) {
                        isRememberedUser = true;
                    }

                    if (orderContainsSubscriptionItem.length > 0 && flagState['is-subscriptions-skipping-addons'] && !isAuth) {
                        yield put(triggerInterstitialLogin(history, true));
                    } else if (flagState['is-native-interstitial-page-enabled'] && !isAuth && !isRememberedUser) {
                        yield put(triggerInterstitialLogin(history));
                    } else if (cartCount > 1) {
                        yield call(history.push, `/checkout/cart/${cartId || ''}`);
                    } else {
                        yield call(history.push, `/checkout/shipping/${cartId || ''}`);
                    }
                }
            }

            // Retrive current cart
            // Send cart snapshot to cart track event
            const fdOrder = yield select(getCartOrder);
            yield call(trackCartUpdate, flagState, 'EventAddToCart', fdOrder);
        } else {
            console.log('Error adding Item To Cart');
        }
    } catch (ex) {
        const cartId = yield select(commonSelectors.getOrderId);
        const {
            recipients = [], // Set a default value to prevent object destructuring error args = { recipients, item, history };
            item, // array
            history,
        } = action.payload;
        yield put(callHandleCartFailure(ex));

        mbpLogger.logError({
            recipients,
            item,
            history,
            appName: process.env.npm_package_name,
            module: 'AddToCart-Operations',
            function: 'onAddToCartNewCartService',
            jsError: ex,
            message: 'onAddToCartNewCartService Failed',
            cartId,
        });

        if (item?.[0]?.brandCode === '1030' || item?.[0]?.brandCode === '1033' || item?.[0]?.personalization?.personalizationType === 'EXTERNAL') {
            removePmallItem();
        }
        const { response } = ex;
        if (response
            && response.status
            && ['401', '403'].includes(response.status.toString())
            && !circuitBreakAddToCartFood()) {
            mbpLogger.logError({
                function: 'workerAddToCartNewCartServices',
                appName: process.env.npm_package_name,
                module: 'Checkout-Operations',
                message: `workerAddToCartNewCartServices Failed - Status code: ${response.status} proceeding to get access token`,
                orderId: cartId,
            });
            yield call(handleAuthorizationApiFailures, workerAddToCartNewCartServices, { recipients, item, history });
        }

        yield put(addToCartActions.loadAddToCartloader(false));
        yield put(addToCartActions.addToCartFailed(ex));
    }
}

// Watchers

function* watchMbpAddToCartChannel() {
    if (typeof window !== 'undefined') {
        while (true) {
            const action = yield take(mbpAddToCartChannel);
            yield put(action);
        }
    }
}

function* watchAddAddonsToCart() {
    let action = yield take(addToCartActions.addAddonsToCart().type);
    while (action !== END) {
        yield fork(onAddAddonsToCart, action.payload);
        action = yield take(addToCartActions.addAddonsToCart().type);
    }
}

function* watchAddToCart() {
    let action = yield take(addToCartActions.addToCart().type);
    while (action !== END) {
        yield fork(onAddToCart, action.payload);
        action = yield take(addToCartActions.addToCart().type);
    }
}

function* watchMbpAddToCart() {
    let action = yield take(addToCartActions.mbpAddToCart().type);
    while (action !== END) {
        yield fork(onMbpAddToCart, action.payload);
        action = yield take(addToCartActions.mbpAddToCart().type);
    }
}

function* watchAddWrapUpsToCart() {
    let action = yield take(addToCartActions.addWrapUpsToCart().type);
    while (action !== END) {
        yield fork(onAddWrapupsToCart, action.payload);
        action = yield take(addToCartActions.addWrapUpsToCart().type);
    }
}

function* watchAddAddonsAndWrapUpsToCart() {
    let action = yield take(addToCartActions.addAddonsAndWrapupToCart().type);
    while (action !== END) {
        yield fork(onAddAddonsAndWrapupToCart, action.payload);
        action = yield take(addToCartActions.addAddonsAndWrapupToCart().type);
    }
}

function* watchAddToCartFood() {
    let action = yield take(addToCartActions.addToCartFood().type);
    while (action !== END) {
        yield fork(onAddToCartFood, action.payload);
        action = yield take(addToCartActions.addToCartFood().type);
    }
}

function* watchAddToCartFailure() {
    let action = yield take(addToCartActions.addToCartFailed().type);
    while (action !== END) {
        yield fork(workerLogAddToCartError, action.payload);
        action = yield take(addToCartActions.addToCartFailed().type);
    }
}

function* watchOnBuyAgain() {
    let action = yield take(addToCartActions.buyAgain().type);
    while (action !== END) {
        yield fork(workerOnBuyAgain, action.payload);
        action = yield take(addToCartActions.buyAgain().type);
    }
}

function* watchAddToCartNewCartServices() {
    yield takeLatest(addToCartActions.addToCartNewCartServices().type, workerAddToCartNewCartServices);
}

const watchers = [
    fork(watchAddToCart),
    fork(watchAddAddonsToCart),
    fork(watchAddWrapUpsToCart),
    fork(watchAddAddonsAndWrapUpsToCart),
    fork(watchAddToCartFood),
    fork(watchAddToCartFailure),
    fork(watchAddToCartNewCartServices),
    fork(watchOnBuyAgain),
    fork(watchMbpAddToCartChannel),
    fork(watchMbpAddToCart),
];

export {
    watchers,
    addToCartOnEvent,
};

export default {};
